import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Moon, Droplets, Sparkles, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Auto-fill last 30 days of missing sleep/water data using the backend
 * heuristic. Extracted from Settings.jsx (line 1018-1107). Keeps its own
 * local "which row is loading" state in the parent (autoFilling / setAutoFilling).
 */
export function AutoFillCard({ language, autoFilling, setAutoFilling }) {
    const isDe = language === 'de';

    const runAutoFill = async (categories, label) => {
        setAutoFilling(label);
        try {
            const res = await axios.post(
                `${API}/api/backfill/autofill`,
                { categories, days: 30 },
                { },
            );
            if (label === 'sleep') {
                toast.success(`${res.data.generated.sleep_entries} ${isDe ? 'Schlaf-Einträge generiert' : 'sleep entries generated'}`);
            } else if (label === 'water') {
                toast.success(`${res.data.generated.water_entries} ${isDe ? 'Wasser-Einträge generiert' : 'water entries generated'}`);
            } else {
                toast.success(res.data.message);
            }
        } catch (_e) {
            toast.error(isDe ? 'Fehler beim Auto-Fill' : 'Auto-fill error');
        } finally {
            setAutoFilling(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 icon-primary" />
                    {isDe ? 'Auto-Fill (30 Tage)' : 'Auto-Fill (30 Days)'}
                </CardTitle>
                <CardDescription>
                    {isDe
                        ? 'Füllt fehlende Schlaf- und Wasser-Daten der letzten 30 Tage automatisch basierend auf deinen vorhandenen Einträgen'
                        : 'Automatically fills missing sleep and water data for the last 30 days based on your existing entries'}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                <AutoFillRow
                    icon={<Moon className="w-5 h-5 icon-sleep" />}
                    bgClass="module-sleep"
                    title={isDe ? 'Schlaf Auto-Fill' : 'Sleep Auto-Fill'}
                    subtitle={isDe ? 'Basierend auf deinem Ø Schlafmuster' : 'Based on your avg sleep pattern'}
                    onClick={() => runAutoFill(['sleep'], 'sleep')}
                    loading={autoFilling === 'sleep'}
                    disabled={!!autoFilling}
                    label={isDe ? 'Füllen' : 'Fill'}
                    testId="autofill-sleep-btn"
                />
                <AutoFillRow
                    icon={<Droplets className="w-5 h-5 icon-travel" />}
                    bgClass="module-travel"
                    title={isDe ? 'Wasser Auto-Fill' : 'Water Auto-Fill'}
                    subtitle={isDe ? 'Basierend auf deinem Ø Wasserkonsum' : 'Based on your avg water intake'}
                    onClick={() => runAutoFill(['water'], 'water')}
                    loading={autoFilling === 'water'}
                    disabled={!!autoFilling}
                    label={isDe ? 'Füllen' : 'Fill'}
                    testId="autofill-water-btn"
                />
                <Button
                    onClick={() => runAutoFill(['sleep', 'water'], 'both')}
                    disabled={!!autoFilling}
                    className="w-full gap-2"
                    data-testid="autofill-both-btn"
                >
                    {autoFilling === 'both' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {isDe ? 'Beides Auto-Füllen (Schlaf + Wasser)' : 'Auto-Fill Both (Sleep + Water)'}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                    {isDe ? 'Bereits vorhandene Tage werden übersprungen' : 'Days with existing data will be skipped'}
                </p>
            </CardContent>
        </Card>
    );
}

function AutoFillRow({ icon, bgClass, title, subtitle, onClick, loading, disabled, label, testId }) {
    return (
        <div className={`flex items-center justify-between p-3 rounded-lg ${bgClass}`}>
            <div className="flex items-center gap-3">
                {icon}
                <div>
                    <p className="font-medium">{title}</p>
                    <p className="text-sm text-muted-foreground">{subtitle}</p>
                </div>
            </div>
            <Button
                onClick={onClick}
                disabled={disabled}
                variant="outline"
                className="gap-2"
                data-testid={testId}
            >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {label}
            </Button>
        </div>
    );
}
